import { ADMIN_ROLES, CHANGELOG_ROLES, type MeResponse, type PermissionCode, type RoleCode, isFacilityRole } from "@seagames/contracts";
import type { LinkProps } from "@tanstack/react-router";
import type { ParseKeys } from "i18next";
import type { ComponentType } from "react";
import {
	BedLineIcon,
	BuildingIcon,
	BusIcon,
	CalendarClockIcon,
	CardIcon,
	ClipboardCheckIcon,
	DoorOpenIcon,
	GaugeIcon,
	HandIcon,
	HashIcon,
	HistoryIcon,
	HomeIcon,
	IdCardIcon,
	KeyIcon,
	LayersIcon,
	LayoutDashboardIcon,
	LifebuoyIcon,
	LinkIcon,
	ListIcon,
	ListOrderedIcon,
	MapPinIcon,
	MealIcon,
	NetworkIcon,
	PackageIcon,
	PeopleIcon,
	PlaneLandingIcon,
	PrinterIcon,
	ReportIcon,
	SettingsIcon,
	ShapesIcon,
	ShieldCheckIcon,
	ShoppingBagIcon,
	TargetIcon,
	TrophyIcon,
	UserCogIcon,
	UserIcon,
	UserPlusIcon,
	UsersIcon,
	WarehouseIcon,
} from "../icons/sidebar-icons";
import { ENTRIES_PAGE_PERMISSIONS } from "../../lib/entries/access";
import { FNB_PAGE_PERMISSIONS } from "../../lib/fnb/access";
import { canSeeChangelog } from "../../lib/changelog/access";
import { canSeeMerch } from "../../lib/merch/access";
import { TRANSPORT_PAGE_PERMISSIONS } from "../../lib/transport/access";
import { HELPDESK_PAGE_PERMISSIONS } from "../helpdesk/access";
import { TRAVEL_PAGE_PERMISSIONS, groupGrants } from "../../lib/travel/access";

/**
 * The navigation tree — one source of truth for the sidebar AND the shell
 * top bar's default heading, so a renamed module renames in both places.
 *
 * Pure module data: entries carry `labelKey`s into the `nav` namespace, and
 * the components translate at render, so this file never touches i18n state.
 * `NavKey` is derived from `en/nav.json`, so a typo'd key fails typecheck.
 *
 * `roles` (optional, on any item or leaf) limits who SEES the entry, by the
 * session's ACTIVE role — never the union of held roles. `permissions`
 * (optional) also lets in a session whose active GROUP grants any of them
 * (the group counts only while the active role is its own, like everywhere):
 * an entry with both is shown to either. `when` (optional) is one more
 * condition the session must ALSO meet — a server verdict read from
 * `/auth/me` (Merchandising: `merch_access`), never a re-derived rule. It is
 * a courtesy, not the control: the API answers 403 regardless, and route
 * guards redirect.
 *
 * One menu (user, 2026-10-30: the old P tree is gone, T is the menu):
 * `SECTIONS_T_SECTORS` for the admins and a NOC, `SECTIONS_T` for every
 * other role. The heading lookup searches both, so a page reached from
 * either still names itself.
 *
 * `shortKey` is the rail's one-word caption under the icon ("Records");
 * without one the rail shows the full label. `badge` is a count slot (the
 * prototype's yellow numbers) — nothing feeds it yet, and nothing should
 * until there is a real count to show.
 */
export type NavKey = ParseKeys<"nav">;
export type Icon = ComponentType<{ className?: string }>;
type NavSession = Pick<MeResponse, "active_role" | "group"> & Partial<Pick<MeResponse, "merch_access" | "changelog_access">>;
/** An extra condition on an entry, ANDed with `roles` / `permissions`. */
export type NavWhen = (session: NavSession) => boolean;
/** A group's page. `icon`: drawn before the label (every page row has one since 2026-10-23). */
export type Leaf = {
	to: LinkProps["to"];
	labelKey: NavKey;
	icon?: Icon;
	exact?: boolean;
	roles?: readonly RoleCode[];
	permissions?: readonly PermissionCode[];
	when?: NavWhen;
};
export type Item =
	| {
			kind: "leaf";
			to: LinkProps["to"];
			labelKey: NavKey;
			shortKey?: NavKey;
			icon: Icon;
			exact?: boolean;
			badge?: number;
			roles?: readonly RoleCode[];
			permissions?: readonly PermissionCode[];
			when?: NavWhen;
	  }
	| {
			kind: "group";
			key: string;
			labelKey: NavKey;
			shortKey?: NavKey;
			icon: Icon;
			children: readonly Leaf[];
			roles?: readonly RoleCode[];
			permissions?: readonly PermissionCode[];
			when?: NavWhen;
	  };
/** `collapsible` sections fold under their label (the prototype's chevron); `key` names that state. */
export type Section = {
	key: string;
	labelKey: NavKey;
	items: readonly Item[];
	collapsible?: boolean;
	/**
	 * A NOC sector (1–4): drawn as a numbered step of the sidebar's stepper —
	 * a disc with this number, the group's short name, the discs joined by a
	 * line — instead of a "SECTOR N" label over a group (user, 2026-10-23).
	 */
	sector?: number;
	/** No label: the rows sit under a thin divider (one lone row needs no heading). */
	bare?: boolean;
	roles?: readonly RoleCode[];
	permissions?: readonly PermissionCode[];
	when?: NavWhen;
};

/**
 * Roles that see Transportation by role alone: the admins, and a transport
 * company's login (a facility role: reading its trips and fleet needs no
 * permission, so the role IS the reason, PLAN X17 — pressing Start / Arrived
 * also needs `transport.dispatch` in its group, decided on the page).
 * Everyone else needs a group with `transport.dispatch` / `transport.request`.
 */
const TRANSPORT_ROLES: readonly RoleCode[] = [...ADMIN_ROLES, "TRANSPORT_PROVIDER"];

/**
 * Accommodation (M11) by role alone — the same people `accommodationView`
 * (lib/accommodation/access.ts) gives a page: the admins, a NOC (its own
 * rooming, read-only) and a hotel's login (a facility role: its front desk).
 * Everyone else needs a group with one of `ACCOMMODATION_PAGE_PERMISSIONS`
 * (the host office, the desk, the helpdesk).
 */
const ACCOMMODATION_ROLES: readonly RoleCode[] = [...ADMIN_ROLES, "ORGANIZATION", "HOTEL"];
const ACCOMMODATION_PAGE_PERMISSIONS = ["accommodation.manage", "accommodation.desk", ...HELPDESK_PAGE_PERMISSIONS] as const satisfies readonly PermissionCode[];

/**
 * The Logistics Desk is the host's, never a NOC's (routes/_app/logistics.desk.tsx
 * sends a NOC home): a NOC group may hold `logistics.travel` for its own
 * travel, which alone would draw the row (user, 2026-10-31: it did, and the
 * click went nowhere).
 */
const NOT_NOC: NavWhen = (s) => s.active_role !== "ORGANIZATION";

/**
 * Administration › Game Settings — the reference data the Games run on. One
 * list, in both trees. SUPER_ADMIN only (user, 2026-10-30: ADMIN no
 * longer sees it; the shell's guard sends anyone else from /settings home).
 */
const GAME_SETTINGS_ROLES: readonly RoleCode[] = ["SUPER_ADMIN"];
const GAME_SETTINGS: readonly Leaf[] = [
	// Not in the menu, routes kept: Countries (fixed data), Registration
	// Windows (to be revamped), Para Classes (`/settings/sport-classes`).
	// Organization Types is a tab of Responsible Organizations, Events a tab
	// of Sports, Zone Access holds the codes and the rules.
	{ to: "/settings/responsible-organizations", labelKey: "items.responsibleOrganizations", icon: BuildingIcon },
	{ to: "/settings/sub-agencies", labelKey: "items.subAgencies", icon: NetworkIcon },
	{ to: "/settings/affiliated-organizations", labelKey: "items.affiliatedOrganizations", icon: LinkIcon },
	{ to: "/settings/functions", labelKey: "items.functions", icon: ShapesIcon },
	{ to: "/settings/zone-access", labelKey: "items.zoneAccess", icon: ShieldCheckIcon },
	{ to: "/settings/handover-locations", labelKey: "items.handoverLocations", icon: HandIcon },
	{ to: "/settings/entry-points", labelKey: "items.entryPoints", icon: DoorOpenIcon },
	// 0087: the airlines flights are drawn with, and their logos.
	{ to: "/settings/airlines", labelKey: "items.airlines", icon: PlaneLandingIcon },
	{ to: "/settings/quotas", labelKey: "items.quotas", icon: GaugeIcon },
	{ to: "/settings/entry-windows", labelKey: "items.entryWindows", icon: CalendarClockIcon },
	// M7c: which parts of the GMS NOCs see (SUPER_ADMIN moves, ADMIN reads).
	{ to: "/settings/noc-stages", labelKey: "items.nocStages", icon: ListOrderedIcon },
	{ to: "/settings/sports", labelKey: "items.sports", icon: TrophyIcon },
	{ to: "/settings/venues", labelKey: "items.venues", icon: MapPinIcon },
];

/**
 * Merchandising: the official store's desk (orders, products, promotions,
 * customers, outbox, staff — tabs on the page). Shown only when
 * `/auth/me.merch_access` says so: SUPER_ADMIN, or an ADMIN on the merch
 * staff list (the API's own SQL rule, never re-derived here). Its own
 * section under a divider — no label over one row.
 */
const MERCH_LEAF: Item = {
	kind: "leaf",
	to: "/merchandising",
	labelKey: "items.merchandising",
	shortKey: "short.merchandising",
	icon: ShoppingBagIcon,
	when: canSeeMerch,
};

const MERCH_SECTION_T: Section = {
	key: "merchandising",
	labelKey: "sections.merchandising",
	bare: true,
	when: canSeeMerch,
	items: [MERCH_LEAF],
};

/**
 * Change Logs, last in every menu under a divider. Shown when
 * `/auth/me.changelog_access` says so (SUPER_ADMIN, or a role the setting
 * lists); `roles` names every GMS role so a facility login (which sees only
 * entries naming its role) gets it too when the setting allows.
 */
const CHANGELOG_SECTION: Section = {
	key: "changelog",
	labelKey: "sections.changelog",
	bare: true,
	when: canSeeChangelog,
	items: [{ kind: "leaf", to: "/changelog", labelKey: "items.changelog", shortKey: "short.changelog", icon: HistoryIcon, roles: CHANGELOG_ROLES }],
};

/**
 * Central Report Engine, directly below the Dashboard in every menu (client,
 * 2026-10-30). Only its place moved: a NOC still finds it locked until
 * Sector 4 (`/reports` → the contingent module, `lib/noc-stages/access.ts`).
 */
const REPORTS_T = { kind: "leaf", to: "/reports", labelKey: "items.reports", shortKey: "short.reports", icon: ReportIcon } as const satisfies Item;

/**
 * Brought over from the old P menu (user, 2026-10-29). Inventory and WSL
 * Inventory are placeholders (Central Report Engine is built: /reports); Incident Helpdesk is
 * the M11 helpdesk for those who may use it, a placeholder for everyone else
 * (routes/_app/incident-helpdesk.tsx). "Super admin only" is the MENU's
 * choice: the pages themselves keep their own rules (ADMIN still reads Users).
 * P's Volunteers placeholders were not brought over (their routes remain).
 */
const SUPER_ADMIN_ONLY: readonly RoleCode[] = ["SUPER_ADMIN"];
const INCIDENT_HELPDESK_T = { kind: "leaf", to: "/incident-helpdesk", labelKey: "items.incidentHelpdesk", shortKey: "short.helpdesk", icon: LifebuoyIcon } as const satisfies Item;
const MY_PROFILE: Item = {
	kind: "group",
	key: "profile",
	labelKey: "groups.profile",
	shortKey: "short.profile",
	icon: UserIcon,
	children: [
		{ to: "/profile", labelKey: "items.profile", icon: UserIcon },
		{ to: "/change-password", labelKey: "items.changePassword", icon: KeyIcon },
	],
};

/**
 * Administration, last in both trees: Game Settings, Users and the
 * inventories (super admin), and My Profile (everyone).
 */
const ADMINISTRATION_T: Section = {
	key: "administration",
	labelKey: "sections.administration",
	collapsible: true,
	items: [
		{ kind: "group", key: "settings", labelKey: "groups.settings", shortKey: "short.settings", icon: SettingsIcon, roles: GAME_SETTINGS_ROLES, children: GAME_SETTINGS },
		{
			kind: "group",
			key: "users",
			labelKey: "groups.users",
			shortKey: "short.users",
			icon: UsersIcon,
			roles: SUPER_ADMIN_ONLY,
			children: [
				{ to: "/users", labelKey: "items.viewUsers", icon: UsersIcon, exact: true },
				{ to: "/users/modules", labelKey: "items.modules", icon: LayersIcon },
				{ to: "/users/groups", labelKey: "items.userGroups", icon: UserCogIcon },
				// Every login's sign-ins, with the app each came from (user, 2026-10-29).
				{ to: "/users/sign-ins", labelKey: "items.signInLog", icon: HistoryIcon },
			],
		},
		{ kind: "leaf", to: "/inventory", labelKey: "items.inventory", shortKey: "short.inventory", icon: PackageIcon, roles: SUPER_ADMIN_ONLY },
		{ kind: "leaf", to: "/wsl-inventory", labelKey: "items.wslInventory", shortKey: "short.wslInventory", icon: WarehouseIcon, roles: SUPER_ADMIN_ONLY },
		MY_PROFILE,
	],
};

/**
 * T — the designer's prototype menu (SEAGAMES-UI `renderNav`), item for item,
 * plus Game Settings at the end (the prototype has no settings section).
 * Pages it has that the GMS does not yet are placeholders. Logistics is
 * Super Admin only in the prototype; here it is ADMIN_ROLES, like Settings —
 * except Arrival & Departure (M9), which the travel desks' groups also see
 * (`logistics.travel` / `logistics.travel_review`), and Transportation (M10):
 * the transport desk and NOC trip requests (`transport.dispatch` /
 * `transport.request`) and the transport companies' logins — and
 * Accommodation (M11): admins, a NOC, a hotel's login, and the
 * `accommodation.*` / `helpdesk.*` groups. Its Helpdesk is a tab there, as
 * in the prototype.
 */
const OVERVIEW_T: Section = {
	key: "overview",
	labelKey: "sections.overview",
	items: [{ kind: "leaf", to: "/", labelKey: "items.dashboard", shortKey: "short.home", icon: HomeIcon, exact: true }, REPORTS_T],
};

export const SECTIONS_T: readonly Section[] = [
	OVERVIEW_T,
	{
		key: "accreditation",
		labelKey: "sections.accreditation",
		collapsible: true,
		items: [
			{
				kind: "leaf",
				to: "/accreditation/entries",
				labelKey: "items.entriesDashboard",
				shortKey: "short.entriesDashboard",
				icon: LayoutDashboardIcon,
				exact: true,
				roles: ADMIN_ROLES,
			},
			{
				kind: "leaf",
				to: "/accreditation/entries/eoi",
				labelKey: "items.expressionOfInterest",
				shortKey: "short.eoi",
				icon: TargetIcon,
				roles: ADMIN_ROLES,
				permissions: ENTRIES_PAGE_PERMISSIONS,
			},
			{
				kind: "leaf",
				to: "/accreditation/entries/ebn",
				labelKey: "items.entryByNumber",
				shortKey: "short.ebn",
				icon: HashIcon,
				roles: ADMIN_ROLES,
				permissions: ENTRIES_PAGE_PERMISSIONS,
			},
			{
				kind: "leaf",
				to: "/accreditation/add",
				labelKey: "items.registerNewRecord",
				shortKey: "short.register",
				icon: UserPlusIcon,
			},
			{ kind: "leaf", to: "/accreditation/records", labelKey: "items.viewRecords", shortKey: "short.records", icon: ListIcon },
			{
				kind: "leaf",
				to: "/accreditation/shortlist",
				labelKey: "items.shortlist",
				shortKey: "short.shortlist",
				icon: ClipboardCheckIcon,
			},
			{
				kind: "leaf",
				to: "/accreditation/e-accreditation",
				labelKey: "items.eAccreditation",
				shortKey: "short.eAccreditation",
				icon: CardIcon,
			},
		],
	},
	{
		key: "logistics",
		labelKey: "sections.logistics",
		collapsible: true,
		roles: [...TRANSPORT_ROLES, ...ACCOMMODATION_ROLES],
		permissions: [...TRAVEL_PAGE_PERMISSIONS, ...TRANSPORT_PAGE_PERMISSIONS, ...ACCOMMODATION_PAGE_PERMISSIONS, ...FNB_PAGE_PERMISSIONS],
		items: [
			{
				kind: "leaf",
				to: "/logistics/desk",
				labelKey: "items.logisticsDesk",
				shortKey: "short.logisticsDesk",
				icon: ClipboardCheckIcon,
				roles: ADMIN_ROLES,
				permissions: TRAVEL_PAGE_PERMISSIONS,
				when: NOT_NOC,
			},
			{
				kind: "leaf",
				to: "/logistics/arrival-departure",
				labelKey: "items.arrivalDeparture",
				shortKey: "short.arrivals",
				icon: PlaneLandingIcon,
				roles: ADMIN_ROLES,
				permissions: TRAVEL_PAGE_PERMISSIONS,
			},
			{
				kind: "leaf",
				to: "/logistics/transportation",
				labelKey: "items.transportation",
				shortKey: "short.transport",
				icon: BusIcon,
				roles: TRANSPORT_ROLES,
				permissions: TRANSPORT_PAGE_PERMISSIONS,
			},
			{
				kind: "leaf",
				to: "/accommodation",
				labelKey: "items.accommodation",
				shortKey: "short.rooms",
				icon: BedLineIcon,
				roles: ACCOMMODATION_ROLES,
				permissions: ACCOMMODATION_PAGE_PERMISSIONS,
			},
			{
				kind: "leaf",
				to: "/logistics/food-beverages",
				labelKey: "items.foodBeverages",
				shortKey: "short.foodBeverages",
				icon: MealIcon,
				roles: ADMIN_ROLES,
				permissions: FNB_PAGE_PERMISSIONS,
			},
		],
	},
	{
		// Brought over from the old P menu (user, 2026-10-29): for every role but the facility ones.
		key: "services",
		labelKey: "sections.operations",
		collapsible: true,
		items: [INCIDENT_HELPDESK_T],
	},
	MERCH_SECTION_T,
	ADMINISTRATION_T,
	CHANGELOG_SECTION,
];

/**
 * T for the admins and a NOC (client, 2026-10-20): the same pages as
 * `SECTIONS_T`, regrouped by the NOC's journey into four numbered sectors,
 * one group each, drawn as a stepper (user, 2026-10-23: short names, a disc
 * per sector), the order of contracts' NOC_STAGES (user, 2026-10-29):
 * 1 Entries the Entries Dashboard, EOI and EBN; 2 Accreditation records,
 * registration, Shortlist and e-Accreditation; 3 Logistics arrival &
 * departure, transport, rooms and F&B; 4 Contingent the Contingent
 * Dashboard and Incident Helpdesk. Every sector OPENS with its own
 * "Dashboard" (user, 2026-10-31: that sector's data only;
 * `lib/dashboard/sectors.ts`) — Sector 4's is the Contingent Dashboard — so
 * Overview keeps Central Report Engine (client, 2026-10-30; still staged at
 * Sector 4), under the overall Dashboard (user, 2026-10-31: every sector at
 * a glance; the admins' and a NOC's), and "/" sends these roles to their dashboard.
 * "Sector N" (Sector Management's word) stays in each step's tooltip. Other
 * roles keep `SECTIONS_T`.
 */
const SECTOR_ROLES: readonly RoleCode[] = [...ADMIN_ROLES, "ORGANIZATION"];

// The overall Dashboard — every sector at a glance — sits above Central Report Engine: the admins' first, a NOC's too (user, 2026-10-31).
const OVERVIEW_T_SECTORS: Section = {
	key: "overview",
	labelKey: "sections.overview",
	items: [{ kind: "leaf", to: "/dashboard", labelKey: "items.dashboard", shortKey: "short.home", icon: HomeIcon, exact: true, roles: SECTOR_ROLES }, REPORTS_T],
};

const SECTIONS_T_SECTORS: readonly Section[] = [
	OVERVIEW_T_SECTORS,
	{
		key: "sector-1",
		labelKey: "sections.sector1",
		sector: 1,
		items: [
			{
				kind: "group",
				key: "early-planning",
				labelKey: "groups.sectorEntries",
				shortKey: "short.entries",
				icon: TargetIcon,
				children: [
					// The admins' Entries analytics, or a NOC's own two phases (components/dashboard/entries-sector.tsx).
					{ to: "/dashboard/entries", labelKey: "items.dashboard", icon: LayoutDashboardIcon },
					{ to: "/accreditation/entries/eoi", labelKey: "items.expressionOfInterest", icon: TargetIcon, roles: ADMIN_ROLES, permissions: ENTRIES_PAGE_PERMISSIONS },
					{ to: "/accreditation/entries/ebn", labelKey: "items.entryByNumber", icon: HashIcon, roles: ADMIN_ROLES, permissions: ENTRIES_PAGE_PERMISSIONS },
				],
			},
		],
	},
	{
		key: "sector-2",
		labelKey: "sections.sector2",
		sector: 2,
		items: [
			{
				kind: "group",
				key: "approval-verification",
				labelKey: "groups.sectorAccreditation",
				shortKey: "short.accreditation",
				icon: IdCardIcon,
				children: [
					{ to: "/dashboard/accreditation", labelKey: "items.dashboard", icon: LayoutDashboardIcon },
					{ to: "/accreditation/records", labelKey: "items.viewRecords", icon: ListIcon },
					{ to: "/accreditation/add", labelKey: "items.registerNewRecord", icon: UserPlusIcon },
					{ to: "/accreditation/shortlist", labelKey: "items.shortlist", icon: ClipboardCheckIcon },
					// E-Accreditation's Print batches tab, its own row for the super admin (user, 2026-10-29), above E-Accreditation (user, 2026-11-02).
					{ to: "/accreditation/e-accreditation/print-batches", labelKey: "items.acCardPrinting", icon: PrinterIcon, roles: SUPER_ADMIN_ONLY },
					{ to: "/accreditation/e-accreditation", labelKey: "items.eAccreditation", icon: CardIcon },
				],
			},
		],
	},
	{
		key: "sector-3",
		labelKey: "sections.sector3",
		sector: 3,
		items: [
			{
				kind: "group",
				key: "logistics-hospitality",
				labelKey: "groups.sectorLogistics",
				shortKey: "short.logistics",
				icon: PlaneLandingIcon,
				children: [
					// A NOC's: one panel per module below that it may open (components/dashboard/logistics-sector.tsx).
					{ to: "/dashboard/logistics", labelKey: "items.dashboard", icon: LayoutDashboardIcon, roles: ["ORGANIZATION"] },
					// The host's: the same panels on top of its day of the chain, Travel → Transfer → Room per person (user, 2026-10-31: one page).
					{ to: "/logistics/desk", labelKey: "items.logisticsDesk", icon: ClipboardCheckIcon, roles: ADMIN_ROLES, permissions: TRAVEL_PAGE_PERMISSIONS, when: NOT_NOC },
					{ to: "/logistics/arrival-departure", labelKey: "items.arrivalDeparture", icon: PlaneLandingIcon, roles: ADMIN_ROLES, permissions: TRAVEL_PAGE_PERMISSIONS },
					{ to: "/logistics/transportation", labelKey: "items.transportation", icon: BusIcon, roles: TRANSPORT_ROLES, permissions: TRANSPORT_PAGE_PERMISSIONS },
					{ to: "/accommodation", labelKey: "items.accommodation", icon: BedLineIcon, roles: ACCOMMODATION_ROLES, permissions: ACCOMMODATION_PAGE_PERMISSIONS },
					{ to: "/logistics/food-beverages", labelKey: "items.foodBeverages", icon: MealIcon, roles: ADMIN_ROLES, permissions: FNB_PAGE_PERMISSIONS },
				],
			},
		],
	},
	{
		key: "sector-4",
		labelKey: "sections.sector4",
		sector: 4,
		items: [
			{
				kind: "group",
				key: "contingent-management",
				labelKey: "groups.sectorContingent",
				shortKey: "short.contingent",
				icon: PeopleIcon,
				children: [
					// Sector 4 · the delegation in one page: a NOC its own, admins pick a NOC.
					{ to: "/contingent", labelKey: "items.dashboard", icon: LayoutDashboardIcon, roles: [...ADMIN_ROLES, "ORGANIZATION"] },
					{ to: INCIDENT_HELPDESK_T.to, labelKey: INCIDENT_HELPDESK_T.labelKey, icon: INCIDENT_HELPDESK_T.icon },
				],
			},
		],
	},
	MERCH_SECTION_T,
	ADMINISTRATION_T,
	CHANGELOG_SECTION,
];

type Gate = { roles?: readonly RoleCode[]; permissions?: readonly PermissionCode[]; when?: NavWhen };

/**
 * No gate → everyone; else the active role is listed, or the active group
 * grants a listed permission.
 *
 * A FACILITY role (contracts `FACILITY_ROLES`: a transport company, a hotel;
 * M12 adds dining) is the exception: it sees an entry only when the
 * entry names its role — never an ungated one (Dashboard, Accreditation,
 * placeholders), never by a permission (its group's `transport.dispatch` is
 * for Start / Arrived, not a menu). Sections and groups (`container`) only
 * hold entries: they pass unless their `roles` exclude it, and are dropped
 * when nothing inside is left. The shell redirects any other URL
 * (`facilityRoleRedirect`).
 */
const allows = (gate: Gate, session: NavSession, container = false) => {
	if (gate.when && !gate.when(session)) return false;
	const role = session.active_role;
	if (role !== null && isFacilityRole(role)) return container ? !gate.roles || gate.roles.includes(role) : !!gate.roles?.includes(role);
	return (
		(!gate.roles && !gate.permissions) ||
		(!!gate.roles && role !== null && gate.roles.includes(role)) ||
		(!!gate.permissions && gate.permissions.some((p) => groupGrants(session, p)))
	);
};

/**
 * The menu as `session` may see it (the sectors for the admins and a NOC,
 * else `SECTIONS_T`): sections and entries whose `roles` / `permissions`
 * exclude it are dropped, then any group or section left empty.
 */
export function sectionsFor(session: NavSession): Section[] {
	const out: Section[] = [];
	const role = session.active_role;
	const tree = role !== null && SECTOR_ROLES.includes(role) ? SECTIONS_T_SECTORS : SECTIONS_T;
	for (const section of tree) {
		if (!allows(section, session, true)) continue;
		const items: Item[] = [];
		for (const item of section.items) {
			if (!allows(item, session, item.kind === "group")) continue;
			if (item.kind === "leaf") {
				items.push(item);
				continue;
			}
			const children = item.children.filter((c) => allows(c, session));
			if (children.length > 0) items.push({ ...item, children });
		}
		if (items.length > 0) out.push({ ...section, items });
	}
	return out;
}

export function leafMatches(leaf: Leaf, pathname: string) {
	return leaf.exact ? pathname === leaf.to : pathname === leaf.to || pathname.startsWith(`${leaf.to}/`);
}

/**
 * Pages below a nav leaf that deserve their own last crumb. Matched after the
 * tree; the parent chain comes from the tree, only the tail is named here.
 */
const TAILS: ReadonlyArray<{ test: RegExp; labelKey: NavKey }> = [
	{ test: /^\/users\/new$/, labelKey: "tails.addUser" },
	{ test: /^\/users\/[^/]+$/, labelKey: "tails.user" },
	// A sport's drill-down: "Game Settings › Disciplines", under the Sports leaf.
	{ test: /^\/settings\/sports\/[^/]+$/, labelKey: "tails.disciplines" },
	// A category's drill-down: its sub-functions and zone access rules.
	{ test: /^\/settings\/functions\/[^/]+$/, labelKey: "tails.category" },
	// Merchandising: one order, one product, under the Merchandising leaf.
	{ test: /^\/merchandising\/orders\/[^/]+$/, labelKey: "tails.merchOrder" },
	{ test: /^\/merchandising\/products\/[^/]+$/, labelKey: "tails.merchProduct" },
];

export interface Crumb {
	labelKey: NavKey;
	to?: LinkProps["to"];
}

/**
 * "Users › View Users", "Accreditation › Add Record", "Users › Add User".
 * Returns [] for a path neither tree knows, so callers can fall back. Both
 * trees are searched; a leaf under a collapsible section takes that section
 * as its parent crumb.
 */
export function breadcrumbsFor(pathname: string): Crumb[] {
	let best: { crumbs: Crumb[]; depth: number } | null = null;
	const consider = (crumbs: Crumb[], leaf: Leaf) => {
		const to = String(leaf.to ?? "");
		const prefix = pathname === to || pathname.startsWith(`${to}/`);
		if (!prefix) return;
		const depth = to.length;
		if (!best || depth > best.depth) best = { crumbs, depth };
	};
	for (const section of [...SECTIONS_T, ...SECTIONS_T_SECTORS]) {
		const parent: Crumb[] = section.collapsible ? [{ labelKey: section.labelKey }] : [];
		for (const item of section.items) {
			if (item.kind === "leaf") consider([...parent, { labelKey: item.labelKey, to: item.to }], item);
			else
				for (const c of item.children)
					consider([{ labelKey: item.labelKey }, { labelKey: c.labelKey, to: c.to }], c);
		}
	}
	if (!best) return [];
	const { crumbs, depth } = best as { crumbs: Crumb[]; depth: number };
	const tail = pathname.length > depth ? TAILS.find((t) => t.test.test(pathname)) : undefined;
	if (tail) {
		// Replace the leaf crumb with the tail's label, under the same parent.
		const parent = crumbs.length > 1 ? crumbs.slice(0, -1) : crumbs.map((c) => ({ ...c }));
		return [...parent, { labelKey: tail.labelKey }];
	}
	return crumbs;
}

/**
 * The shell top bar's default heading for a path: the page's own nav label as
 * the title, and the nav section it lives in as the small line under it —
 * "View Records / Accreditation", "Countries / Game Settings",
 * "Accommodation / Operations". `subtitleKey` is null for a top-level leaf in
 * the Overview section (the Dashboard), which the bar subtitles with the
 * Games' name instead. Null for a path neither tree knows.
 */
export interface PageHeading {
	titleKey: NavKey;
	subtitleKey: NavKey | null;
}

export function pageHeadingFor(pathname: string): PageHeading | null {
	const crumbs = breadcrumbsFor(pathname);
	const last = crumbs[crumbs.length - 1];
	if (!last) return null;
	const parent = crumbs[crumbs.length - 2];
	if (parent) return { titleKey: last.labelKey, subtitleKey: parent.labelKey };
	const section = [...SECTIONS_T, ...SECTIONS_T_SECTORS].find(
		(s) =>
			s.key !== "overview" &&
			s.items.some((i) => i.kind === "leaf" && i.labelKey === last.labelKey && leafMatches(i, pathname)),
	);
	return { titleKey: last.labelKey, subtitleKey: section?.labelKey ?? null };
}
